import { buildDefaultClausesFromGroups } from './clauseBuilder.ts';
import { buildDefaultGroups } from './filterGroups.ts';
import { and, or, type WhereCondition } from './filterHelpers.ts';

type BuildWithProviderIdStrategyParams = {
  alwaysFilters: WhereCondition[];
  directProviderFilters: WhereCondition;
  dataTypeFilters: WhereCondition[];
  filterGroupClauses: WhereCondition[];
  accountId: string;
  marketPrefilter?: WhereCondition;
};

type BuildWithoutProviderIdStrategyParams = {
  alwaysFilters: WhereCondition[];
  accountId: string;
  providerId?: string;
  dataTypeFilters: WhereCondition[];
  filterGroupClauses: WhereCondition[];
  isOaMatchType: boolean;
  marketPrefilter?: WhereCondition;
};

export const buildWithProviderIdStrategy = ({
  alwaysFilters,
  directProviderFilters,
  dataTypeFilters,
  filterGroupClauses,
  marketPrefilter,
}: BuildWithProviderIdStrategyParams): WhereCondition => {
  if (dataTypeFilters.length > 0 || filterGroupClauses.length > 0) {
    const basicFilter = buildBasicFilter(dataTypeFilters, filterGroupClauses);

    return and(...alwaysFilters, and(basicFilter, directProviderFilters), marketPrefilter);
  }

  return and(...alwaysFilters, directProviderFilters, marketPrefilter);
};

export const buildWithoutProviderIdStrategy = ({
  alwaysFilters,
  accountId,
  providerId,
  dataTypeFilters,
  filterGroupClauses,
  isOaMatchType,
  marketPrefilter,
}: BuildWithoutProviderIdStrategyParams): WhereCondition => {
  // Always build default segment selection (predefined, sellable, direct)
  const defaultGroups = buildDefaultGroups(accountId, providerId, isOaMatchType);
  const defaultClauses = buildDefaultClausesFromGroups(defaultGroups);

  // Data type filters target segments by type and should not be gated by the default
  // sub_category allowlist — applying AND(defaultClauses, dataTypeFilter) would produce
  // zero results for types like purchase_propensity that are not in the default groups.
  if (dataTypeFilters.length > 0) {
    const basicFilter = buildBasicFilter(dataTypeFilters, filterGroupClauses);
    return and(...alwaysFilters, basicFilter, marketPrefilter);
  }

  if (filterGroupClauses.length > 0) {
    return and(...alwaysFilters, and(or(...defaultClauses), and(...filterGroupClauses)), marketPrefilter);
  }

  return and(...alwaysFilters, or(...defaultClauses), marketPrefilter);
};

export const buildBasicFilter = (
  dataTypeFilters: WhereCondition[],
  filterGroupClauses: WhereCondition[]
): WhereCondition => {
  if (dataTypeFilters.length === 0) {
    return and(...filterGroupClauses);
  }

  if (filterGroupClauses.length === 0) {
    return or(...dataTypeFilters);
  }

  return and(or(...dataTypeFilters), and(...filterGroupClauses));
};
